import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NAMA_BULAN, NAMA_HARI, gridBulan } from "@/lib/kalenderRotasi";

// Grid satu bulan (Senin pertama), satu tombol per tanggal. Tanda: Stase tersimpan (abu), Stase baru
// (tosca), Jaga (titik amber: penuh = baru, cincin = tersimpan), rentang yg sedang dipilih (bingkai).
// Tanpa dependensi kalender -- semua aturan di lib/kalenderRotasi.js & server.
function keteranganHari(t) {
    const bagian = [];
    if (t?.staseTersimpan) bagian.push("Stase tersimpan");
    if (t?.staseBaru) bagian.push("Stase baru");
    if (t?.jagaTersimpan) bagian.push("Jaga tersimpan");
    if (t?.jagaBaru) bagian.push("Jaga baru");
    return bagian.join(", ");
}

function kelasHari(t, terpilih, aktif) {
    if (!aktif) return "text-slate-300 cursor-not-allowed";
    if (terpilih) return "ring-2 ring-[#00B9AD] bg-[#00B9AD]/20 text-slate-900 font-semibold";
    if (t?.staseBaru) return "bg-[#00B9AD]/30 text-slate-900 font-semibold";
    if (t?.staseTersimpan) return "bg-slate-200 text-slate-800";
    return "text-slate-700 hover:bg-slate-100";
}

export default function KalenderBulan({ tahun, bulan, tanda, pilihan, hariAktif, onKlik, onGeser, ditekan, hariIni }) {
    const minggu = gridBulan(tahun, bulan);
    const dalamPilihan = (d) => pilihan?.awal && d >= pilihan.awal && d <= (pilihan.akhir || pilihan.awal);
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-2 w-full max-w-[340px]" data-testid="rc-kalender">
            <div className="flex items-center justify-between mb-1">
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onGeser(-1)}
                    aria-label="Bulan sebelumnya" data-testid="rc-kalender-sebelum">
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-sm font-semibold text-slate-800" aria-live="polite" data-testid="rc-kalender-judul">
                    {NAMA_BULAN[bulan - 1]} {tahun}
                </div>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onGeser(1)}
                    aria-label="Bulan berikutnya" data-testid="rc-kalender-berikut">
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
            <table className="w-full table-fixed border-separate border-spacing-0.5" role="grid"
                aria-label={`Kalender ${NAMA_BULAN[bulan - 1]} ${tahun}`}>
                <thead>
                    <tr>{NAMA_HARI.map((h) => <th key={h} className="text-[10px] font-semibold text-slate-500 py-1">{h}</th>)}</tr>
                </thead>
                <tbody>
                    {minggu.map((baris, i) => (
                        <tr key={i}>
                            {baris.map((d, j) => {
                                if (!d) return <td key={j} />;
                                const t = tanda[d];
                                const aktif = hariAktif(d);
                                const ket = keteranganHari(t);
                                return (
                                    <td key={d} className="p-0">
                                        <button
                                            type="button" disabled={!aktif} onClick={() => onKlik(d)}
                                            aria-pressed={ditekan ? ditekan(d) : undefined}
                                            aria-label={`${Number(d.slice(8))} ${NAMA_BULAN[bulan - 1]} ${tahun}${ket ? `, ${ket}` : ""}`}
                                            className={`relative w-full h-9 rounded text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[#267B84] ${kelasHari(t, dalamPilihan(d), aktif)} ${d === hariIni ? "underline" : ""}`}
                                            data-testid={`rc-hari-${d}`}
                                        >
                                            {Number(d.slice(8))}
                                            {(t?.jagaBaru || t?.jagaTersimpan) && (
                                                <span aria-hidden="true" className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${t.jagaBaru ? "bg-amber-500" : "border border-amber-500"}`} />
                                            )}
                                        </button>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500" aria-hidden="true">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-200" /> Stase tersimpan</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#00B9AD]/30" /> Stase baru</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full border border-amber-500" /> Jaga tersimpan</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Jaga baru</span>
            </div>
        </div>
    );
}
